import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import BackButton from '../../components/common/BackButton';
import {
    FaBell,
    FaCheckCircle,
    FaExclamationTriangle,
    FaNewspaper,
    FaUser,
    FaIdCard,
    FaHeading,
    FaAlignLeft,
    FaPaperPlane,
    FaLayerGroup
} from 'react-icons/fa';

const SendNotifications = () => {
    const { t, i18n } = useTranslation();
    const [notificationType, setNotificationType] = useState('news');
    const [formData, setFormData] = useState({
        title: '',
        body: '',
        personalNumber: '',
    });
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleTypeChange = (e) => {
        setNotificationType(e.target.value);
        setMessage({ type: '', text: '' });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage({ type: '', text: '' });

        try {
            let endpoint = '';
            let payload = {
                title: formData.title,
                body: formData.body,
            };

            if (notificationType === 'news') {
                endpoint = 'https://hasanhaji.com/api/notifications/news';
            } else {
                endpoint = 'https://hasanhaji.com/api/notifications/personal';
                payload.personalNumber = formData.personalNumber;
            }

            await axios.post(endpoint, payload);

            setMessage({ type: 'success', text: t('notifications.successMessage') });
            setFormData({ title: '', body: '', personalNumber: '' });
        } catch (err) {
            console.error('Error sending notification:', err);
            setMessage({
                type: 'error',
                text: err.response?.data?.message || t('notifications.errorMessage'),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container py-5">
            <div className="row justify-content-center">
                <div className="col-lg-8 col-md-10">
                    <BackButton to="/admin" />

                    <div className="card border-0 rounded-4 overflow-hidden shadow-sm">
                        <div
                            className="card-header text-white text-center py-4"
                            style={{ background: 'linear-gradient(135deg, var(--primary-color) 0%, #6a1b9a 100%)' }}
                        >
                            <h3 className="mb-0 fw-bold d-flex align-items-center justify-content-center gap-2">
                                <FaBell />
                                <span>{t('notifications.title')}</span>
                            </h3>
                            <p className="mb-0 opacity-75 mt-2">{t('notifications.settings')}</p>
                        </div>

                        <div className="card-body p-4 p-md-5 bg-white">
                            {message.text && (
                                <div className={`alert ${message.type === 'success' ? 'alert-success' : 'alert-danger'} d-flex align-items-center mb-4 rounded-3`} role="alert">
                                    {message.type === 'success' ? <FaCheckCircle className="me-2 fs-5" /> : <FaExclamationTriangle className="me-2 fs-5" />}
                                    <div>{message.text}</div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div className="mb-4">
                                    <label className="form-label fw-bold text-muted small">
                                        {t('notifications.typeLabel')}
                                    </label>
                                    <div className="d-flex gap-3">
                                        <div
                                            className={`flex-fill p-3 rounded-3 border text-center cursor-pointer transition-all ${notificationType === 'news' ? 'border-primary bg-primary bg-opacity-10 text-primary' : 'bg-light text-muted'}`}
                                            onClick={() => setNotificationType('news')}
                                            style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                                        >
                                            <FaNewspaper className="fs-2 mb-2" />
                                            <div className="fw-bold">{t('notifications.typeNews')}</div>
                                            <input
                                                type="radio"
                                                name="notificationType"
                                                value="news"
                                                checked={notificationType === 'news'}
                                                onChange={handleTypeChange}
                                                className="d-none"
                                            />
                                        </div>
                                        <div
                                            className={`flex-fill p-3 rounded-3 border text-center cursor-pointer transition-all ${notificationType === 'personal' ? 'border-primary bg-primary bg-opacity-10 text-primary' : 'bg-light text-muted'}`}
                                            onClick={() => setNotificationType('personal')}
                                            style={{ cursor: 'pointer', transition: 'all 0.3s' }}
                                        >
                                            <FaUser className="fs-2 mb-2" />
                                            <div className="fw-bold">{t('notifications.typePersonal')}</div>
                                            <input
                                                type="radio"
                                                name="notificationType"
                                                value="personal"
                                                checked={notificationType === 'personal'}
                                                onChange={handleTypeChange}
                                                className="d-none"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="row g-4">
                                    {notificationType === 'personal' && (
                                        <div className="col-12 animate-slide-in">
                                            <label className="form-label fw-bold text-muted small">{t('notifications.personalNumberLabel')}</label>
                                            <div className="input-group">
                                                <span className="input-group-text bg-light border-end-0"><FaIdCard className="text-secondary" /></span>
                                                <input
                                                    type="text"
                                                    className="form-control border-start-0 ps-0 bg-light"
                                                    id="personalNumber"
                                                    name="personalNumber"
                                                    value={formData.personalNumber}
                                                    onChange={handleChange}
                                                    placeholder={t('notifications.personalNumberPlaceholder')}
                                                    required={notificationType === 'personal'}
                                                />
                                            </div>
                                        </div>
                                    )}

                                    <div className="col-12">
                                        <label className="form-label fw-bold text-muted small">{t('notifications.notificationTitle')}</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0"><FaHeading className="text-secondary" /></span>
                                            <input
                                                type="text"
                                                className="form-control border-start-0 ps-0 bg-light"
                                                id="title"
                                                name="title"
                                                value={formData.title}
                                                onChange={handleChange}
                                                placeholder={t('notifications.titlePlaceholder')}
                                                required
                                            />
                                        </div>
                                    </div>

                                    <div className="col-12">
                                        <label className="form-label fw-bold text-muted small">{t('notifications.notificationBody')}</label>
                                        <div className="input-group">
                                            <span className="input-group-text bg-light border-end-0 align-items-start pt-2"><FaAlignLeft className="text-secondary" /></span>
                                            <textarea
                                                className="form-control border-start-0 ps-0 bg-light"
                                                id="body"
                                                name="body"
                                                rows="4"
                                                value={formData.body}
                                                onChange={handleChange}
                                                placeholder={t('notifications.bodyPlaceholder')}
                                                required
                                                style={{ resize: 'none' }}
                                            ></textarea>
                                        </div>
                                    </div>

                                    <div className="col-12 mt-4">
                                        <button
                                            type="submit"
                                            className="btn w-100 py-3 text-white fw-bold"
                                            disabled={loading}
                                            style={{
                                                background: loading ? '#6c757d' : 'var(--primary-color)',
                                                transition: 'all 0.3s ease',
                                                transform: loading ? 'none' : 'translateY(0)',
                                            }}
                                            onMouseOver={(e) => !loading && (e.currentTarget.style.transform = 'translateY(-2px)')}
                                            onMouseOut={(e) => !loading && (e.currentTarget.style.transform = 'translateY(0)')}
                                        >
                                            {loading ? (
                                                <>
                                                    <span
                                                        className="spinner-border spinner-border-sm me-2"
                                                        role="status"
                                                        aria-hidden="true"
                                                    ></span>
                                                    {t('notifications.sending')}
                                                </>
                                            ) : (
                                                <>
                                                    <FaPaperPlane className={`me-2 ${i18n.dir() === 'rtl' ? 'ms-2 me-0' : 'me-2'}`} />
                                                    {t('notifications.sendButton')}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
            <style jsx>{`
                .animate-slide-in { animation: slideIn 0.3s ease-out; }
                @keyframes slideIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default SendNotifications;
